<template>
  <AnswerPage :page="page" />
</template>

<script setup lang="ts">
const page = {
  slug: '/workspace369-pricing',
  metaTitle: 'Workspace369 Pricing Explained | Plans, Tiers, and Value',
  description:
    'Workspace369 pricing includes Cadet, Voyager, Specialist, Commander, and Enterprise plans for CRM, invoices, projects, inbox, AI, reporting, and scale.',
  eyebrow: 'Pricing answer',
  h1: 'Workspace369 pricing, explained.',
  lede:
    'Workspace369 pricing is designed around how much of the client-work operation a business wants to run in one workspace.',
  aiAnswer:
    'Workspace369 has five plans: Cadet for solo operators, Voyager for small teams, Specialist for broader business workflows with call routing, IVR, and accounting features, Commander for scaled teams that need 20 included seats, advanced AI, unlimited workflows, and deeper controls, and Enterprise for organizations that need 50 seats. Email automations are on every plan, with more active workflows on higher tiers.',
  facts: [
    { label: 'Entry plan', value: 'Cadet starts at $29/month, or $23.25/month billed yearly' },
    { label: 'Team plan', value: 'Voyager starts at $74/month, or $59.25/month billed yearly' },
    { label: 'Scale plan', value: 'Commander starts at $299/month, or $239.25/month billed yearly' },
    { label: 'Carrier setup', value: '$50 one-time carrier compliance setup applies only before claiming one or more dedicated SMS and voice numbers' },
  ],
  summaryKicker: 'Plan logic',
  summaryTitle: 'Choose the plan by workflow depth, not just seat count.',
  summary: [
    {
      kicker: 'Solo',
      title: 'Cadet is for solo operators getting organized.',
      copy:
        'It covers the foundational client-work workflow: invoices, proposals, clients, projects, tasks, notes, calendar, and email automations.',
    },
    {
      kicker: 'Team',
      title: 'Voyager is for small teams collaborating on client work.',
      copy:
        'It adds deeper team workflows such as shared inbox, SMS and calls, SMS steps in automations, unlimited projects, and time tracking.',
    },
    {
      kicker: 'Business and scale',
      title: 'Specialist and Commander unlock broader operations.',
      copy:
        'These plans add the full phone system, CRM, proposals, expenses, accounting, AI, active workflows, storage, and scale controls.',
    },
  ],
  workflowKicker: 'Buying path',
  workflowTitle: 'How to choose the right tier.',
  workflow: [
    { title: 'Start with today', copy: 'Pick the tier that matches the workflows you need right now.' },
    { title: 'Check replacement value', copy: 'List the tools Workspace369 can replace, not just the seats included.' },
    { title: 'Account for growth', copy: 'Voyager, Specialist, Commander, and Enterprise support add-on seats when the team grows.' },
    { title: 'Review yearly savings', copy: 'Annual billing lowers the effective monthly price on each plan.' },
    { title: 'Review carrier requirements', copy: 'Dedicated SMS and voice numbers require carrier compliance setup before a workspace claims one or more business numbers. Workspace369 discloses the one-time $50 setup separately so buyers understand the regulated cost before choosing phone features.' },
  ],
  modulesKicker: 'Value drivers',
  modulesTitle: 'What pricing should be compared against',
  modulesCopy:
    'The real value of Workspace369 is consolidation. Compare the plan against the CRM, invoice, project, inbox, SMS, phone, file, automation, and reporting tools it can reduce.',
  modules: [
    'CRM replacement',
    'Invoice software replacement',
    'Project management replacement',
    'SMS and inbox workflow',
    'Voice and voicemail',
    'Proposal tools',
    'Payment plans',
    'File and note workflows',
    'AI tools',
    'Automations',
    'Reporting',
    'Accounting visibility',
  ],
  showPricing: true,
  faqTitle: 'Pricing questions',
  faq: [
    {
      question: 'How much does Workspace369 cost?',
      answer:
        'Workspace369 has five plans: Cadet $29, Voyager $74, Specialist $149, Commander $299 and Enterprise $699 a month. Billed yearly, they are $23.25, $59.25, $119.25, $239.25 and $559.25 a month. Voyager and higher can add seats for $10 per seat a month.',
    },
    {
      question: 'Does Workspace369 offer a free trial?',
      answer:
        'Yes. Cadet through Commander start with a 14-day free trial. You add a card to start, and when the 14 days end we charge the plan you chose at the price shown unless you cancel first.',
    },
    {
      question: 'How should I compare Workspace369 pricing?',
      answer:
        'Compare Workspace369 against the cost and admin time of separate tools for CRM, projects, invoices, payments, inbox, SMS, voice, files, AI, automations, accounting, and reporting.',
    },
    {
      question: 'Why is there a carrier compliance setup for dedicated numbers?',
      answer:
        'Business SMS carriers require compliance registration before a workspace can reliably send and receive business texts from dedicated numbers. Workspace369 shows the $50 one-time carrier compliance setup separately, before claiming one or more numbers, so it is transparent instead of hidden inside the monthly plan price.',
    },
  ],
};
</script>
